import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Settings, Share2, Printer } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PlayCard } from "@/components/PlayCard";
import { PlayFilters, usePlayFilters } from "@/components/PlayFilters";
import { ConfirmDialog } from "@/components/ConfirmDialog";

export default function Playbook() {
  const { teamId } = useParams();
  const nav = useNavigate();
  const { setActiveTeam, loadTeams } = useAuth();
  const [team, setTeam] = useState(null);
  const [plays, setPlays] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const f = usePlayFilters(plays || []);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([api.get(`/teams/${teamId}`), api.get(`/teams/${teamId}/plays`)]);
      setTeam(t.data); setPlays(p.data); setActiveTeam(teamId);
    } catch (e) { toast.error(errMsg(e)); nav("/teams"); }
  }, [teamId, nav]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const create = async (category) => {
    try { const { data } = await api.post(`/teams/${teamId}/plays`, { category }); loadTeams(); nav(`/play/${data.id}/edit`); } catch (e) { toast.error(errMsg(e)); }
  };
  const favorite = async (p) => {
    setPlays((ps) => ps.map((x) => (x.id === p.id ? { ...x, favorite: !p.favorite } : x)));
    try { await api.patch(`/plays/${p.id}/favorite`, { favorite: !p.favorite }); } catch (e) { toast.error(errMsg(e)); load(); }
  };
  const duplicate = async (p) => {
    try { const { data } = await api.post(`/plays/${p.id}/duplicate`); setPlays((ps) => [data, ...ps]); loadTeams(); toast.success(`Created "${data.name}"`); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    const p = toDelete;
    try { await api.delete(`/plays/${p.id}`); setPlays((ps) => ps.filter((x) => x.id !== p.id)); loadTeams(); toast.success("Play deleted"); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!plays) return <p className="text-slate-400" data-testid="playbook-loading">Loading playbook…</p>;
  const isOwner = team.can_manage;
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div><p className="label">{team.name} · {team.player_count} per side{!isOwner && " · Assistant coach"}</p><h1 className="h1">Playbook</h1></div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/teams/${teamId}/print${f.filtered.length !== plays.length ? `?plays=${f.filtered.map((p) => p.id).join(",")}` : ""}`} className="btn btn-ghost" data-testid="btn-print-playbook"><Printer size={18} />Print</Link>
          {isOwner && <Link to={`/teams/${teamId}/settings`} className="btn btn-ghost" data-testid="btn-share-playbook"><Share2 size={18} />Share</Link>}
          <Link to={`/teams/${teamId}/settings`} className="btn btn-ghost" aria-label="Team settings" data-testid="playbook-settings-link"><Settings size={18} /></Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><button className="btn btn-primary" data-testid="btn-new-play"><Plus size={18} />New play</button></DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#111D35] border-[#1F3358] text-white">
              {[["offense", "Offense play"], ["defense", "Defense play"], ["special", "Special teams play"]].map(([c, l]) => (
                <DropdownMenuItem key={c} className="min-h-[44px] cursor-pointer" onSelect={() => create(c)} data-testid={`new-play-${c}`}>{l}</DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <PlayFilters f={f} />
      {!f.filtered.length && <div className="card p-8 text-center text-slate-400" data-testid="playbook-empty">No plays match. Try clearing filters or create a new play.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="playbook-grid">
        {f.filtered.map((p) => (
          <PlayCard key={p.id} play={p} team={team} learnTo={`/play/${p.id}/learn`} editTo={`/play/${p.id}/edit`}
            onFavorite={() => favorite(p)} onDuplicate={() => duplicate(p)} onDelete={isOwner ? () => setToDelete(p) : undefined} printTo={`/teams/${teamId}/print?plays=${p.id}`} />
        ))}
      </div>
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Delete this play?" body={`"${toDelete?.name}" will be removed from the playbook for good.`} confirmLabel="Delete play" onConfirm={remove} testId="confirm-delete-play" />
    </div>
  );
}
